import {
  createRootRouteWithContext,
  Link,
  Outlet,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { useAuth, UserButton } from "@clerk/clerk-react";
import { useEffect } from "react";
import { BulkToolbar } from "../components/bulk-toolbar";
import { PeriodBar } from "../components/period-bar";
import { useAppSession } from "../lib/session";

interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
});

const NAV_LINKS = [
  { to: "/", label: "Dashboard" },
  { to: "/entries", label: "Écritures" },
  { to: "/pnl", label: "P&L" },
  { to: "/balance", label: "Bilan" },
  { to: "/indicators", label: "Indicateurs" },
  { to: "/issues", label: "À décider" },
  { to: "/sync", label: "Sync" },
  { to: "/manual", label: "Manuel" },
  { to: "/splits", label: "Ventilations" },
  { to: "/rules", label: "Règles" },
  { to: "/cards", label: "Cartes", superAdminOnly: true },
] as const;

function RootLayout() {
  const location = useLocation();
  const isSignInRoute = location.pathname.startsWith("/sign-in");

  if (isSignInRoute) {
    return <Outlet />;
  }

  return (
    <Guard>
      <AppLayout />
    </Guard>
  );
}

function Guard({ children }: { children: React.ReactNode }) {
  const { isLoaded, isSignedIn, signOut } = useAuth();
  const { canAccessCompta } = useAppSession();
  const navigate = useNavigate();

  // Un utilisateur connecté sans droits (client, autre team) est déconnecté :
  // l'instance Clerk est partagée avec cartage-mono, la session seule ne
  // prouve rien.
  useEffect(() => {
    if (!isLoaded) return;
    if (!isSignedIn) {
      void navigate({ to: "/sign-in" });
      return;
    }
    if (!canAccessCompta) {
      void signOut().then(() => navigate({ to: "/sign-in" }));
    }
  }, [isLoaded, isSignedIn, canAccessCompta, signOut, navigate]);

  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500">
        Chargement…
      </div>
    );
  }
  if (!isSignedIn || !canAccessCompta) return null;

  return <>{children}</>;
}

function AppLayout() {
  const { isSuperAdmin } = useAppSession();
  const links = NAV_LINKS.filter((link) => !("superAdminOnly" in link) || isSuperAdmin);
  return (
    <div className="bg-gray-50 min-h-screen">
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex">
              <div className="flex-shrink-0 flex items-center">
                <span className="text-xl font-bold text-gray-900">Cartage Compta</span>
              </div>
              <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
                {links.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    activeProps={{
                      className:
                        "border-blue-500 text-gray-900 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium",
                    }}
                    inactiveProps={{
                      className:
                        "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium",
                    }}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
            <div className="flex items-center">
              <UserButton afterSignOutUrl="/sign-in" />
            </div>
          </div>
        </div>
      </nav>
      <PeriodBar />
      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <Outlet />
      </main>
      <BulkToolbar />
    </div>
  );
}
